/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * TimeTextField
 *
 * @file TimeTextField.jsx
 * @author Satyaki Bose
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  ClockIcon,
  Label,
  PeriodButton,
  SegmentedInput,
  Separator,
  TimeFieldWrapper,
  TimeSegment,
  TimeWrapper
} from './Styles/AetTimeFieldStyles';
import { TIMEFIELD_CONSTANTS } from '../../common/constants';
import { useTimeInput } from '../../hooks/useTimeInput';

const {
  MAX_INPUT_LENGTH,
  FIELD_TYPES
} = TIMEFIELD_CONSTANTS;

const TimeTextField = ({
  label,
  value,
  onChange,
  parentApp,
  icons,
  className,
  hasError,
  hasSuccess
}) => {
  const {
    displayHour,
    displayMinute,
    displayPeriod,
    hourRef,
    minuteRef,
    periodRef,
    handleHourChange,
    handleMinuteChange,
    handlePeriodClick,
    handleHourFocus,
    handleMinuteFocus,
    handlePeriodFocus,
    handleHourBlur,
    handleMinuteBlur,
    handlePeriodBlur,
    handleKeyDown
  } = useTimeInput({ value, onChange, periodButtonClassName: undefined });

  if (parentApp && parentApp.toLowerCase() !== 'aet') {
    return null;
  }

  return (
    <TimeWrapper className={className || ''}>
      {label && <Label>{label}</Label>}
      <TimeFieldWrapper hasError={hasError} hasSuccess={hasSuccess}>
        <SegmentedInput>
          <TimeSegment
            ref={hourRef}
            type="text"
            value={displayHour}
            onChange={handleHourChange}
            onFocus={handleHourFocus}
            onBlur={handleHourBlur}
            onKeyDown={(e) => handleKeyDown(e, FIELD_TYPES.HOUR)}
            maxLength={MAX_INPUT_LENGTH}
            inputMode="numeric"
            aria-label="Hour"
          />
          <Separator>:</Separator>
          <TimeSegment
            ref={minuteRef}
            type="text"
            value={displayMinute}
            onChange={handleMinuteChange}
            onFocus={handleMinuteFocus}
            onBlur={handleMinuteBlur}
            onKeyDown={(e) => handleKeyDown(e, FIELD_TYPES.MINUTE)}
            maxLength={MAX_INPUT_LENGTH}
            inputMode="numeric"
            aria-label="Minute"
          />
          <PeriodButton
            type="button"
            ref={periodRef}
            onClick={handlePeriodClick}
            onFocus={handlePeriodFocus}
            onBlur={handlePeriodBlur}
            onKeyDown={(e) => handleKeyDown(e, FIELD_TYPES.PERIOD)}
            aria-label="AM/PM"
          >
            {displayPeriod}
          </PeriodButton>
        </SegmentedInput>
        {icons?.clock && (
          <ClockIcon>
            {icons.clock}
          </ClockIcon>
        )}
      </TimeFieldWrapper>
    </TimeWrapper>
  );
};

TimeTextField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  parentApp: PropTypes.string,
  icons: PropTypes.object,
  className: PropTypes.string,
  hasError: PropTypes.bool,
  hasSuccess: PropTypes.bool
};

TimeTextField.defaultProps = {
  label: '',
  parentApp: 'aet',
  icons: null,
  className: '',
  hasError: false,
  hasSuccess: false
};

export default TimeTextField;
